import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs, Paper, TextField, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';

const corporateDeals = [
  {
    id: 1,
    channel: 'Social',
    partnerName: 'Meta',
    deal: 'For $500,000 spend receive 10% discount rate',
    spendGoal: '$500,000',
    timePeriod: 'Jan 2024 - Dec 2024',
    constraint: 'Must include equal split between Instagram and Facebook'
  },
  {
    id: 2,
    channel: 'Display - Online Ad',
    partnerName: 'Yahoo',
    deal: 'For $250,000 spend receive 7% discount rate',
    spendGoal: '$250,000',
    timePeriod: 'Q1 2024',
    constraint: 'Homepage placements only'
  },
  {
    id: 3,
    channel: 'Audio - Music Streaming',
    partnerName: 'Spotify',
    deal: 'For $150,000 spend receive 15% discount rate',
    spendGoal: '$150,000',
    timePeriod: 'H1 2024',
    constraint: 'Premium inventory only'
  },
  {
    id: 4,
    channel: 'CTV',
    partnerName: 'Hulu',
    deal: 'For $1,000,000 spend receive 12% discount rate',
    spendGoal: '$1,000,000',
    timePeriod: 'FY 2024',
    constraint: 'Does not include live sports'
  },
  {
    id: 5,
    channel: 'Display - Online Ad',
    partnerName: 'ESPN',
    deal: 'For $300,000 spend receive 8% discount rate',
    spendGoal: '$300,000',
    timePeriod: 'Q2 2024',
    constraint: 'Does not include NFL coverage'
  }
];

const preferredPartners = [
  { id: 1, channel: 'Social', partnerName: 'TikTok' },
  { id: 2, channel: 'Display - Online Ad', partnerName: 'The Trade Desk' },
  { id: 3, channel: 'Audio - Music Streaming', partnerName: 'Pandora' },
  { id: 4, channel: 'CTV', partnerName: 'Roku' },
  { id: 5, channel: 'Display - Online Ad', partnerName: 'Google DV360' }
];

const blacklistedPartners = [
  { id: 1, channel: 'Social', partnerName: 'Truth Social' },
  { id: 2, channel: 'Display - Online Ad', partnerName: 'AdNetwork XYZ' },
  { id: 3, channel: 'Audio - Music Streaming', partnerName: 'StreamCo' },
  { id: 4, channel: 'CTV', partnerName: 'ViewersChoice' },
  { id: 5, channel: 'Display - Online Ad', partnerName: 'ClickMedia' }
];

const corporateColumns = [
  { field: 'channel', headerName: 'Channel', width: 200, sortable: true },
  { field: 'partnerName', headerName: 'Partner Name', width: 150, sortable: true },
  { field: 'deal', headerName: 'Deal', width: 300, sortable: true },
  { field: 'spendGoal', headerName: 'Contracted Spend Goal', width: 200, sortable: true },
  { field: 'timePeriod', headerName: 'Time Period', width: 150, sortable: true },
  { field: 'constraint', headerName: 'Constraint', width: 300, sortable: true }
];

const simpleColumns = [
  { field: 'channel', headerName: 'Channel', width: 200, sortable: true },
  { field: 'partnerName', headerName: 'Partner Name', width: 200, sortable: true }
];

export function DealsPage() {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [corporateSelectedRows, setCorporateSelectedRows] = useState([]);
  const [preferredSelectedRows, setPreferredSelectedRows] = useState([]);

  const handleAddToConsiderationSet = () => {
    // Get selected corporate deals
    const selectedCorporatePartners = corporateDeals
      .filter(partner => corporateSelectedRows.includes(partner.id))
      .map(partner => ({
        partner: partner.partnerName,
        hasData: false
      }));

    // Get selected preferred partners
    const selectedPreferredPartners = preferredPartners
      .filter(partner => preferredSelectedRows.includes(partner.id))
      .map(partner => ({
        partner: partner.partnerName,
        hasData: false
      }));

    // Combine all selected partners
    const selectedPartners = [...selectedCorporatePartners, ...selectedPreferredPartners];

    // Get existing partners from localStorage
    const existingPartners = JSON.parse(localStorage.getItem('scorecardPartners') || '[]');
    
    // Add new partners
    const updatedPartners = [...existingPartners, ...selectedPartners];
    
    // Save to localStorage
    localStorage.setItem('scorecardPartners', JSON.stringify(updatedPartners));
    
    // Clear selections
    setCorporateSelectedRows([]);
    setPreferredSelectedRows([]);

    // Navigate to RFP Scorecard page
    navigate('/partners/display/rfp-scorecard');
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(row => 
      Object.values(row).some(value => 
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  return (
    <main className="main-content">
      <h1 className="page-title">Deals</h1>
      
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search deals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="deals-search"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: '#666', mr: 1 }} />
            ),
          }}
        />
        {(currentTab === 0 || currentTab === 1) && 
         (corporateSelectedRows.length > 0 || preferredSelectedRows.length > 0) && (
          <Button 
            variant="contained" 
            onClick={handleAddToConsiderationSet}
            className="button-primary"
            sx={{ textTransform: 'none' }}
          >
            Add to Consideration Set ({corporateSelectedRows.length + preferredSelectedRows.length})
          </Button>
        )}
      </div>

      <Paper elevation={0} className="deals-container">
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          className="deals-tabs"
        >
          <Tab label="Corporate Deals" />
          <Tab label="Preferred Partners" />
          <Tab label="Partner Blacklist" />
        </Tabs>

        <div className="table-container">
          {currentTab === 0 && (
            <DataGrid
              rows={filterData(corporateDeals)}
              columns={corporateColumns}
              checkboxSelection
              onRowSelectionModelChange={setCorporateSelectedRows}
              rowSelectionModel={corporateSelectedRows}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          )}
          {currentTab === 1 && (
            <DataGrid
              rows={filterData(preferredPartners)}
              columns={simpleColumns}
              checkboxSelection
              onRowSelectionModelChange={setPreferredSelectedRows}
              rowSelectionModel={preferredSelectedRows}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          )}
          {currentTab === 2 && (
            <DataGrid
              rows={filterData(blacklistedPartners)}
              columns={simpleColumns}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          )}
        </div>
      </Paper>
    </main>
  );
} 